import { ChevronLeft, ChevronRight, MoreVertical } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Spinner } from "../../../components/ui/common";
import { formatCurrency } from "../../../lib/utils";
import { DailyData, GraphResponse, MonthlyData } from "../@types";
import {
  useGetExpenseGraphDataMutation,
  useGetIncomeGraphDataMutation,
} from "../financeApiSlice";
import FinanceGraph02 from "./FinanceGraph";

type ViewMode = "daily" | "monthly";

interface FinanceGraphProps {
  title: string;
  data: GraphResponse | undefined;
  onFetch: (year: number, month: number) => void;
  type: "income" | "expense";
  isLoading?: boolean;
}

// Reusable Finance Graph Component
const FinanceGraph: React.FC<FinanceGraphProps> = ({
  title,
  data,
  onFetch,
  type,
  isLoading = false,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>("daily");
  const [showDropdown, setShowDropdown] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const xAxisScrollRef = React.useRef<HTMLDivElement>(null);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  useEffect(() => {
    onFetch(currentDate.getFullYear(), currentDate.getMonth() + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, viewMode]);

  const handlePrevious = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: "smooth" });
    }
    if (xAxisScrollRef.current) {
      xAxisScrollRef.current.scrollBy({ left: -200, behavior: "smooth" });
    }
  };

  const handleNext = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: "smooth" });
    }
    if (xAxisScrollRef.current) {
      xAxisScrollRef.current.scrollBy({ left: 200, behavior: "smooth" });
    }
  };

  const getDisplayData = () => {
    if (!data) return [];
    return viewMode === "daily" ? data.dailyData : data.monthlyData;
  };

  const displayData = getDisplayData();
  const maxAmount = Math.max(...displayData.map((d) => d.amount), 0);

  const minAmount = 0; // Minimum y-axis value in Naira
  const chartMaxAmount = Math.max(maxAmount, 100000); // Ensure at least 100k scale

  const formatLabel = (item: DailyData | MonthlyData) => {
    if (viewMode === "daily" && "date" in item) {
      // Date format is "MM-DD" from API
      const [month, day] = item.date.split("-");
      const monthIndex = parseInt(month) - 1;
      return `${monthNames[monthIndex]?.slice(0, 3) || ""} ${parseInt(day)}`;
    }
    if ("month" in item) {
      // Month format is "MMM YYYY" from API (e.g., "Nov 2025")
      return item.month.split(" ")[0];
    }
    return "";
  };

  const getCurrentPeriod = () => {
    if (viewMode === "daily") {
      return `${
        monthNames[currentDate.getMonth()]
      } ${currentDate.getFullYear()}`;
    }
    return currentDate.getFullYear().toString();
  };

  // Use the currentPeriod from API response if available
  const displayPeriod = data?.currentPeriod || getCurrentPeriod();

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setCurrentDate(date);
      setShowDatePicker(false);
    }
  };

  const handleCalendarOpen = () => {
    document.body.classList.add("datepicker-open");
  };

  const handleCalendarClose = () => {
    document.body.classList.remove("datepicker-open");
    setShowDatePicker(false);
  };

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm flex-1 min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-gray-500 text-sm font-medium">{title}</h3>
            <div className="relative">
              <button
                onClick={() => setShowDatePicker(!showDatePicker)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md flex items-center gap-2 hover:bg-gray-50"
              >
                {displayPeriod}
                <ChevronRight className="w-4 h-4 transform rotate-90" />
              </button>
              {showDatePicker && (
                <div className="absolute top-full left-0 mt-1 z-50">
                  <DatePicker
                    selected={currentDate}
                    onChange={handleDateChange}
                    dateFormat={viewMode === "daily" ? "MMMM yyyy" : "yyyy"}
                    showMonthYearPicker={viewMode === "daily"}
                    showYearPicker={viewMode === "monthly"}
                    inline
                    calendarClassName="shadow-lg border border-gray-200 rounded-lg"
                    onCalendarOpen={handleCalendarOpen}
                    onCalendarClose={handleCalendarClose}
                    onClickOutside={handleCalendarClose}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="text-3xl font-bold">
            {formatCurrency(data?.totalAmount || 0)}
          </div>
        </div>
        <div className="relative">
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <MoreVertical className="w-5 h-5 text-gray-600" />
          </button>
          {showDropdown && (
            <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
              <button
                onClick={() => {
                  setViewMode("daily");
                  setShowDropdown(false);
                }}
                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${
                  viewMode === "daily" ? "bg-gray-50 font-medium" : ""
                }`}
              >
                Daily View
              </button>
              <button
                onClick={() => {
                  setViewMode("monthly");
                  setShowDropdown(false);
                }}
                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${
                  viewMode === "monthly" ? "bg-gray-50 font-medium" : ""
                }`}
              >
                Monthly View
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="relative h-64">
        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex flex-col items-center gap-2">
              <Spinner />
              <span className="text-sm text-gray-500">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-xs text-gray-400">
              <span>₦{chartMaxAmount.toLocaleString()}</span>
              <span>₦{Math.round(chartMaxAmount * 0.8).toLocaleString()}</span>
              <span>₦{Math.round(chartMaxAmount * 0.6).toLocaleString()}</span>
              <span>₦{Math.round(chartMaxAmount * 0.4).toLocaleString()}</span>
              <span>₦{Math.round(chartMaxAmount * 0.2).toLocaleString()}</span>
              <span>₦0</span>
            </div>

            <div className="h-full relative">
              <div
                ref={scrollContainerRef}
                className="h-full flex items-end gap-2 overflow-x-auto overflow-y-hidden scrollbar-hide pl-12"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                onScroll={(e) => {
                  if (xAxisScrollRef.current) {
                    xAxisScrollRef.current.scrollLeft =
                      e.currentTarget.scrollLeft;
                  }
                }}
              >
                {displayData.map((item, index) => {
                  const height =
                    chartMaxAmount > 0
                      ? (item.amount / chartMaxAmount) * 100
                      : 0;
                  const hasValue = item.amount > 0;

                  return (
                    <div
                      key={index}
                      className="flex flex-col items-center h-full"
                      style={{ flexShrink: 0 }}
                    >
                      <div className="w-full flex-1 flex items-end justify-center">
                        <div
                          className={`w-20 rounded-[20px] transition-all ${
                            hasValue ? "bg-primary" : "bg-gray-200"
                          }`}
                          style={{
                            height: `${Math.max(height, 0)}%`,
                            minHeight: "8px",
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* X-axis labels positioned below y-axis */}
            <div className="mt-2 pl-12">
              <div
                ref={xAxisScrollRef}
                className="flex gap-2 overflow-x-auto scrollbar-hide"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                onScroll={(e) => {
                  if (scrollContainerRef.current) {
                    scrollContainerRef.current.scrollLeft =
                      e.currentTarget.scrollLeft;
                  }
                }}
              >
                {displayData.map((item, index) => (
                  <div
                    key={index}
                    className="text-xs text-gray-400 whitespace-nowrap text-center w-20 flex-shrink-0"
                  >
                    {formatLabel(item)}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="flex items-center justify-between mt-6">
        <button
          onClick={handlePrevious}
          className="p-1 hover:bg-gray-100 rounded"
        >
          <ChevronLeft className="w-5 h-5 text-gray-400" />
        </button>
        <div className="flex-1 h-1 bg-gray-200 rounded mx-2">
          <div className="h-full w-1/3 bg-gray-300 rounded" />
        </div>
        <button onClick={handleNext} className="p-1 hover:bg-gray-100 rounded">
          <ChevronRight className="w-5 h-5 text-gray-400" />
        </button>
      </div>
    </div>
  );
};

type FinanceStatisticProps = {
  id?: string;
  idType: string;
};

const FinanceStatistics: React.FC<FinanceStatisticProps> = ({ id, idType }) => {
  const [incomeData, setIncomeData] = useState<GraphResponse>();
  const [expenseData, setExpenseData] = useState<GraphResponse>();

  const [getIncomeGraphData, { isLoading: isLoadingIncome }] =
    useGetIncomeGraphDataMutation();

  const [getExpenseGraphData, { isLoading: isLoadingExpense }] =
    useGetExpenseGraphDataMutation();

  const fetchIncomeData = useCallback(
    async (year: number, month: number) => {
      const result = await getIncomeGraphData({
        vehicleId: idType === "vehicle" ? id : undefined,
        driverId: idType === "driver" ? id : undefined,
        year,
        month,
      });
      const resultData = result?.data?.data;
      if (resultData) {
        setIncomeData(resultData);
      }
    },
    [id, idType, getIncomeGraphData]
  );

  const fetchExpenseData = useCallback(
    async (year: number, month: number) => {
      const result = await getExpenseGraphData({
        vehicleId: idType === "vehicle" ? id : undefined,
        driverId: idType === "driver" ? id : undefined,
        year,
        month,
      });
      const resultData = result?.data?.data;
      if (resultData) {
        setExpenseData(resultData);
      }
    },
    [id, idType, getExpenseGraphData]
  );

  return (
    <div className="">
      <h1 className="text-[18px] font-bold mb-2">Finance statistics</h1>
      <div className="flex flex-col lg:flex-row gap-6 w-full lg:max-w-[82vw]">
        <FinanceGraph02
          title="Total Income"
          data={incomeData}
          onFetch={fetchIncomeData}
          type="income"
          isLoading={isLoadingIncome}
        />
        <FinanceGraph
          title="Total Expenses"
          data={expenseData}
          onFetch={fetchExpenseData}
          type="expense"
          isLoading={isLoadingExpense}
        />
      </div>
    </div>
  );
};

export default FinanceStatistics;
